import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
const key = campaign => `campaign-rest:${campaign}`;
const readPending = campaign => { try { return JSON.parse(sessionStorage.getItem(key(campaign)) || 'null'); } catch { return null; } };
const savePending = (campaign, value) => { try { if (value) sessionStorage.setItem(key(campaign), JSON.stringify(value)); else sessionStorage.removeItem(key(campaign)); } catch { /* Retry remains available in this tab. */ } };
export default function CampaignRestControls({ campaignId }) {
  const [type, setType] = useState(null), [roster, setRoster] = useState(null);
  const [selected, setSelected] = useState([]), [error, setError] = useState('');
  const [busy, setBusy] = useState(false), [pending, setPending] = useState(() => readPending(campaignId));
  const scope = useRef(campaignId), lock = useRef(false), openScope = useRef(null); scope.current = campaignId;
  useEffect(() => { openScope.current = null; setType(null); setRoster(null); setSelected([]); setError(''); setPending(readPending(campaignId)); setBusy(false); }, [campaignId]);
  useEffect(() => {
    if (!type || openScope.current !== campaignId) return;
    let live = true; setRoster(null); setError('');
    (async () => {
      try {
        const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/rests`, { cache: 'no-store' });
        const data = await response.json(); if (!response.ok) throw new Error(data.message || 'Could not load characters.');
        if (live) { setRoster(data.characters || []); setSelected(previous => previous.filter(id => data.characters?.some(c => c.id === id && c.eligible))); }
      } catch (failure) { if (live) setError(failure.message); }
    })();
    return () => { live = false; };
  }, [type, campaignId]);
  const submit = async () => {
    if (lock.current || (!pending && !selected.length)) return;
    lock.current = true; setBusy(true); setError('');
    const campaign = campaignId;
    const request = pending || { id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`, type, characterIds: selected };
    try {
      savePending(campaign, request); setPending(request);
      const response = await apiFetch(`/campaigns/${encodeURIComponent(campaign)}/rests`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) });
      const result = await response.json();
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) { savePending(campaign, null); if (scope.current === campaign) setPending(null); }
        throw new Error(result.message || 'Rest could not be completed.');
      }
      savePending(campaign, null);
      if (scope.current !== campaign) return;
      setPending(null); setSelected([]); setType(null);
      notify(`${result.recipients.length} characters received a ${result.type === 'long' ? 'Long' : 'Short'} Rest.`, 'success');
    } catch (failure) { if (scope.current === campaign) setError(failure.message); }
    finally { lock.current = false; if (scope.current === campaign) setBusy(false); }
  };
  const restType = pending?.type || type;
  const label = restType === 'long' ? 'Long Rest' : 'Short Rest';
  return <>
    <Button variant="outline-light" size="sm" onClick={() => { openScope.current = campaignId; setType(pending?.type || 'long'); }}>Long Rest</Button>
    <Button variant="outline-light" size="sm" onClick={() => { openScope.current = campaignId; setType(pending?.type || 'short'); }}>Short Rest</Button>
    <Modal scrollable show={Boolean(type)} onHide={() => !busy && setType(null)} centered className="dnd-modal modern-modal">
      <Modal.Header closeButton={!busy}><Modal.Title>Grant {label}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>Choose the characters who completed the rest. The DM confirms its duration, interruptions and eligibility, including the Long Rest limit of once per 24 hours.</p>
        {restType === 'short' && <p className="small">Short Rest resources recover. Selected players receive a popup to spend Hit Dice, one at a time, or finish without spending any.</p>}
        {error && <Alert variant="danger">{error}</Alert>}
        {pending && <Alert variant="info">A rest is awaiting confirmation. Retry the same rest safely; recipients are locked.</Alert>}
        {!roster && !error && <Spinner role="status" animation="border"><span className="visually-hidden">Loading characters</span></Spinner>}
        {roster && <fieldset disabled={busy || Boolean(pending)}>
          <Button size="sm" variant="outline-light" className="mb-2" onClick={() => setSelected(roster.filter(c => c.eligible).map(c => c.id))}>Select all eligible</Button>
          {roster.map(c => <Form.Check key={c.id} id={`rest-player-${c.id}`} disabled={!c.eligible}
            checked={(pending?.characterIds || selected).includes(c.id)}
            label={`${c.name} · ${c.currentHp}/${c.maxHp} HP${c.eligible ? '' : ' · Cannot rest at 0 HP or while dead'}`}
            onChange={() => setSelected(previous => previous.includes(c.id) ? previous.filter(id => id !== c.id) : [...previous, c.id])} />)}
          {!roster.length && <p>No characters in this campaign.</p>}
        </fieldset>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setType(null)}>Close</Button>
        <Button disabled={busy || (!pending && (!roster || !selected.length))} onClick={submit}>{busy ? 'Completing…' : pending ? 'Retry rest' : `Grant ${label}`}</Button></Modal.Footer>
    </Modal>
  </>;
}

